/**
 * Tunable constants for the custom color setup fee.
 *
 * The $500 fee is charged once per DISTINCT custom color in an order. OPC
 * prices per configured line and cannot express that, so the fee lives in a
 * cart line this theme owns and derives from cart state.
 *
 * It owns: the property key names that identify a custom-color line, the fee
 * line's own property key, and the settle-chain cap.
 * It does NOT own: any cart reading, any planning, any DOM work. See
 * custom-color-model.js (pure logic) and custom-color-reconciler.js (effects).
 *
 * KEY NAMES ARE EVIDENCE, NOT GUESSES. They were read out of a real /cart.js
 * payload captured from the hosted dev theme and stored at
 * tests/fixtures/custom-color-carts.json. If OPC renames an option, recapture
 * the fixture rather than editing these by inspection — the model fails closed,
 * so a wrong name here costs a missed fee silently.
 *
 * CAPTURED 2026-08-31 from the hosted dev theme, two Shaker Recessed Panel
 * doors configured with the custom color option (colors "OC17" and
 * "sw7005"). The earlier provisional guesses in this file were all wrong:
 * the selection lived under `Color`, not `Finish`; the typed color under
 * `Custom Color Info`, not `Custom Color Name`; and the selection value is
 * not a bare "Custom Color" — see CUSTOM_SELECTION_VALUES below.
 */
(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  } else {
    root.CustomColorConstants = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  // Property keys whose value names the finish choice. `Color` is the one the
  // captured doors use. `Finish` is kept because it is a sibling option on the
  // same set (captured value: "Color + $47 Per Door Sqft [$47.00]") and a
  // differently-labelled drawer-front option set may put the custom-color
  // choice there instead. Order is scan order; the first key whose value looks
  // custom wins.
  const SELECTION_KEYS = ['Finish', 'Color'];

  // Substrings that mean "this is a custom color", matched against a
  // case-folded SELECTION_KEYS value. Stored folded; the cart's value is
  // folded before comparison.
  //
  // SUBSTRING, NOT EQUALITY — this is load-bearing. The captured value is:
  //
  //     "Custom Color - Add Note [$500.00]"
  //
  // Two moving parts are baked into that one string. OPC appends its own price
  // annotation, so the value CHANGES AT CUTOVER: the moment step 2 of the
  // runbook sets the option to $0, "[$500.00]" becomes "[$0.00]" or vanishes.
  // An equality match would detect every line right up to the cutover and then
  // silently stop at the exact moment the fee starts mattering. The merchant
  // can also reword the "- Add Note" half in the OPC admin without telling
  // anyone. Matching a substring survives both.
  const CUSTOM_SELECTION_VALUES = ['custom color', 'custom colour'];

  // Property keys holding the customer's typed color text. `Custom Color Info`
  // is the captured one; the others are kept as fallbacks for option sets that
  // label the same field differently.
  const COLOR_TEXT_KEYS = ['Custom Color Info', 'Custom Color Name', 'Custom Color'];

  // Prefixes for the PATTERN fallback in model.isColorTextKey, matched against
  // a normalized (lowercased) key. Both spellings, because an option-set label
  // is merchant-typed text in another system: this repo's own spelling
  // convention has no reach over it, and a set labelled "Custom Colour Info"
  // must still have its color read. See CUSTOM_SELECTION_VALUES above, which
  // carries both for the same reason.
  const CUSTOM_COLOR_KEY_PREFIXES = ['custom color', 'custom colour'];

  // The property written onto the fee line itself. Visible in the admin and on
  // packing slips, which is the whole point — staff reconcile against it.
  const FEE_COLOR_PROPERTY = 'Custom Color';

  // The class a merchant puts on the custom-color note field in the options
  // app, so the product-page hint can find it.
  //
  // AN EXPLICIT CONTRACT, because there is nothing else stable to hold on to.
  // The app renders no `name` attributes at all, and its ids are per-option
  // hashes (text-676d1de38a238ffe97a72a5a) that change when an option set is
  // rebuilt. An earlier version of the hint searched for
  // [name="properties[Custom Color Info]"] and found the app's HIDDEN mirror
  // input, so it rendered the hint next to that instead of the visible field —
  // under the Add to Cart button, offering a click that filled a field the app
  // regenerates on submit.
  //
  // Add this class to the color note option in EVERY doors and drawer-fronts
  // option set. Where it is absent the hint simply does not render, which
  // costs a convenience and nothing else — the fee is derived from the cart
  // afterwards either way, and never depends on this.
  const COLOR_INPUT_CLASS = 'custom-color-input';

  // NO BYTE BUDGET CONSTANT HERE, ON PURPOSE.
  //
  // An earlier revision carried FEE_LINE_ATTR_BYTES, to charge the fee line
  // against OPC's 20 kB cart-transform output budget (opc-budget-constants.js).
  // Its premise was wrong. opc-budget-model.js's lineCosts() counts only lines
  // passing isAppPricedLine(), which requires the `_opc` marker property. The
  // fee line is a plain Shopify product this theme adds; it carries no `_opc`,
  // so the transform never serializes it and it costs zero output bytes.
  //
  // That is not a hopeful reading of the filter: carts here already mix
  // non-OPC products (hinges, slides, hardware) with OPC-priced doors, and the
  // budget guard has been live and calibrated against real breaches while
  // ignoring every one of them. The fee line is in the same class.
  //
  // If OPC ever starts pricing the fee product, this stops being true and the
  // fee line needs measuring like any other configured line.

  // A reconcile run can legitimately trigger one more run: our own mutations
  // emit cart events. Beyond that, a plan that never empties means something is
  // wrong (a sold-out fee variant, a rejected add) and looping would hammer the
  // cart API. Stop and log instead.
  const MAX_SETTLE_RUNS = 3;

  /** Merchant configuration injected from Liquid. */
  function config() {
    const c = (typeof window !== 'undefined' && window.CustomColorConfig) || {};
    return {
      feeVariantId: typeof c.feeVariantId === 'number' ? c.feeVariantId : null,
    };
  }

  return {
    SELECTION_KEYS,
    CUSTOM_SELECTION_VALUES,
    COLOR_TEXT_KEYS,
    CUSTOM_COLOR_KEY_PREFIXES,
    COLOR_INPUT_CLASS,
    FEE_COLOR_PROPERTY,
    MAX_SETTLE_RUNS,
    config,
  };
});
